// application/useCases/commitChargeMasterVersion.js
//
// Two error classes in one file, against the usual one-per-file rule: they are this use case's own
// refusals — neither is a failure of appending a version in general, which is why they are not in
// `versionErrors` — and a caller handling one imports the other. Splitting them out would scatter one
// operation's contract across three files.
/* eslint-disable max-classes-per-file */
import {
  canTakeEffectAt,
  createVersionTimeline,
  minEffectiveFrom,
  nextVersionAt,
  supersededBySaveAt,
  versionsOnDate,
} from '../../domain/model/VersionTimeline';
import { currencyOf, soleSeriesOf } from '../../domain/services/chargeMasterSeries';
import { EffectiveDateRejected, VersionCollision } from '../ports/versionErrors';

/**
 * How many times a lost version number is recomputed and retried.
 *
 * Three, for the same reason the funder use case picks three: contention here is two people saving
 * the clinic's charge master within a round trip of each other, which resolves on the next attempt.
 * A series that loses three in a row is not one attempt away from succeeding.
 */
const MAX_ATTEMPTS = 3;

/**
 * Raised when a clinic's sheets belong to more than one series and no single one can be resolved.
 *
 * NOT RECOVERABLE BY RETRYING, and not a state this code can produce — it would mean a clinic had
 * acquired a second charge master, which nothing writes today. It exists so that the day that
 * changes, the failure is a visible hold naming the clinic rather than charges quietly drawn from
 * whichever series happened to have the later date.
 */
export class AmbiguousChargeMaster extends Error {
  /** @param {string} clinicId */
  constructor(clinicId) {
    super(`Clinic ${clinicId} has more than one charge master; none can be resolved.`);
    this.name = 'AmbiguousChargeMaster';
    this.clinicId = clinicId;
  }
}

/**
 * A new sheet tried to state its charges in a different currency from the rest of its series.
 *
 * REFUSED, because the alternative is silent and severe. A charge is stored as an integer number of
 * minor units, so 12000 is 120.00 in whatever the sheet says — change the currency and every figure
 * in the series is reinterpreted at once, with the old sheets still reading as the old currency and
 * the new one as something else. Nothing about the stored numbers would look wrong.
 *
 * A CLINIC THAT GENUINELY BILLS IN TWO CURRENCIES NEEDS TWO CHARGE MASTERS, one per billing entity,
 * which is what `chargeMasterId` exists to make expressible. Changing the currency of an existing
 * series is never the way to arrive there.
 */
export class CurrencyChangeRejected extends Error {
  /**
   * @param {string} was - the currency the series is already stated in
   * @param {string} now - the currency the save tried to write
   */
  constructor(was, now) {
    super(`A charge master is stated in ${was}; a sheet cannot change it to ${now}.`);
    this.name = 'CurrencyChangeRejected';
    this.was = was;
    this.now = now;
  }
}

/**
 * Saves a clinic's charge master as of a date, creating the series if it has none.
 *
 * ONE OPERATION FOR CREATE AND APPEND, because the caller cannot usefully tell them apart. A clinic
 * opening the editor for the first time and one amending next year's sheet perform the same act —
 * "this is what we charge, from this date" — and which write it becomes is a fact about storage, not
 * a decision the user makes. Branching here rather than in the composable also means the create path
 * gets the same effective-date handling as every other save instead of quietly skipping it.
 *
 * THE SERIES IS RE-READ HERE, not taken from the caller, and that is the point of the use case
 * rather than an optimization. The editor loaded a timeline when the dialog opened and validated the
 * user's choice against it, but a colleague can schedule a change while the user types: the floor
 * moves, the next version number moves, and a save computed against the stale copy would either be
 * refused by the rules or quietly claim a position that is no longer free.
 *
 * A COLLISION IS RETRIED, an effective date is not. Losing the version number is a race with a
 * correct resolution — re-read, recompute, write again. A rejected date is a decision that has to
 * reach the user, since the only honest response is to show them what the timeline now looks like.
 *
 * NO DATE CHECK ON THE FIRST SHEET, deliberately. `canTakeEffectAt` asks whether a date is available
 * given what already applies, and with no series there is nothing for it to conflict with — a clinic
 * recording that its charges started last January is an ordinary thing to be entering now.
 *
 * @param {{ clinicId: string, asOf: string, effectiveFrom: string,
 *   record: import('../ports/ChargeMasterRepository.port').ChargeMasterRecord }} request - `asOf` is
 *   'YYYY-MM-DD' and comes from the edge; nothing here reads a clock, so the same input always
 *   produces the same decision.
 * @param {import('../ports/ChargeMasterRepository.port').ChargeMasterRepository} repository
 * @returns {Promise<{ chargeMasterId: string, versionId: string }>}
 * @throws {AmbiguousChargeMaster} the clinic has more than one charge master
 * @throws {CurrencyChangeRejected} the sheet states a different currency from the rest of the series
 * @throws {EffectiveDateRejected} the date is not one a new sheet may take
 * @throws {VersionCollision} the version number was taken on every attempt
 */
export async function commitChargeMasterVersion({
  clinicId,
  asOf,
  effectiveFrom,
  record,
}, repository) {
  let lastCollision = null;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    // Sequential by nature: each attempt has to see the result of the one before it.
    // eslint-disable-next-line no-await-in-loop
    const series = soleSeriesOf(await repository.loadVersions({ clinicId }));

    if (series.isAmbiguous) {
      throw new AmbiguousChargeMaster(clinicId);
    }

    // Returned rather than awaited: this leaves the loop, so there is nothing to catch here.
    //
    // A LOST CREATE IS REPORTED, NOT RETRIED, and that is a decision rather than an omission. The
    // series key is the clinic, so two people saving a first sheet on one date aim at one document
    // and the loser gets a `VersionCollision` — which is exactly what stops the clinic acquiring two
    // charge masters. Retrying it would find the winner's series and append to it, turning "create
    // the charge master" into "correct a sheet I have never seen" and silently superseding a
    // colleague's charges. The dialog reports the collision and re-reads, so the user looks at what
    // now exists and decides for themselves.
    if (series.chargeMasterId === null) {
      return repository.create({ clinicId, effectiveFrom, record });
    }
    // The series already states a currency, and a sheet may not change it — every charge in the
    // timeline is an integer number of minor units OF that currency, so changing it reinterprets all
    // of them at once and nothing about the stored numbers would look wrong.
    const seriesCurrency = currencyOf(series.versions);

    if (seriesCurrency !== null && record?.currency !== seriesCurrency) {
      throw new CurrencyChangeRejected(seriesCurrency, record?.currency ?? null);
    }
    const timeline = createVersionTimeline(series.versions);

    if (!canTakeEffectAt(timeline, asOf, effectiveFrom)) {
      throw new EffectiveDateRejected(effectiveFrom, { earliest: minEffectiveFrom(timeline, asOf) });
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      const versionId = await repository.appendVersion({
        chargeMasterId: series.chargeMasterId,
        clinicId,
        effectiveFrom,
        version: nextVersionAt(timeline, effectiveFrom),
        record,
        // Entries already on this date lose the read flag, not their row: an earlier sheet for the
        // same date is still true history, it is simply no longer the one to read.
        supersededVersionIds: versionsOnDate(timeline, effectiveFrom)
          .map((superseded) => superseded.versionId),
        // A sheet scheduled for a DIFFERENT date is canceled outright — the decision the editor
        // warns about before confirming.
        cancelledVersionIds: supersededBySaveAt(timeline, asOf, effectiveFrom)
          .map((cancelled) => cancelled.versionId),
      });

      return { chargeMasterId: series.chargeMasterId, versionId };
    } catch (error) {
      // Anything that is not a lost race is the caller's to handle immediately.
      if (!(error instanceof VersionCollision)) {
        throw error;
      }
      lastCollision = error;
    }
  }

  // Out of attempts. The last collision is rethrown rather than a summary of the loop, so the
  // coordinates that were actually contended survive to whatever reports this.
  throw lastCollision;
} // commitChargeMasterVersion()
